import { useCallback, useReducer, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useFirebase } from '../lib/firebase/FirebaseProvider';
import { functions } from '../lib/firebase/config';
import { ImageAnalysisResponse } from '@curbside-quest/shared';
import { WorkflowService } from '../services/workflowService';
import { useFeatureFlag } from './useFeatureFlag';
import {
  SubmissionWorkflow,
  SubmissionStep,
  SubmissionWorkflowData,
  CapturedPhoto,
  AIAnalysisStepData,
  MetadataStepData,
  WorkflowAction,
  WorkflowState,
  StepNavigation
} from '../types/submission-workflow';

// Workflow reducer
const workflowReducer = (state: WorkflowState, action: WorkflowAction, aiAnalysisEnabled: boolean = true): WorkflowState => {
  switch (action.type) {
    case 'START_WORKFLOW':
      const newWorkflow = action.payload || createNewWorkflow(state.workflow?.userId || '');
      return {
        ...state,
        workflow: newWorkflow,
        isLoading: false,
        error: null,
        navigation: calculateNavigation('photo_capture', newWorkflow.data, aiAnalysisEnabled)
      };

    case 'ADD_PHOTO':
      if (!state.workflow) return state;
      
      // Check if photo with same ID already exists
      const existingPhotoIndex = state.workflow.data.photos.findIndex(p => p.id === action.payload.id);
      let updatedPhotos: CapturedPhoto[];
      
      if (existingPhotoIndex >= 0) {
        // Update existing photo
        updatedPhotos = [...state.workflow.data.photos];
        updatedPhotos[existingPhotoIndex] = action.payload;
      } else {
        // Add new photo
        updatedPhotos = [...state.workflow.data.photos, action.payload];
      }
      
      return {
        ...state,
        workflow: {
          ...state.workflow,
          data: {
            ...state.workflow.data,
            photos: updatedPhotos
          },
          updatedAt: new Date()
        },
        navigation: calculateNavigation(state.workflow.currentStep, {
          ...state.workflow.data,
          photos: updatedPhotos
        }, aiAnalysisEnabled)
      };

    case 'REMOVE_PHOTO':
      if (!state.workflow) return state;
      const filteredPhotos = state.workflow.data.photos.filter(photo => photo.id !== action.payload);
      return {
        ...state,
        workflow: {
          ...state.workflow,
          data: {
            ...state.workflow.data,
            photos: filteredPhotos
          },
          updatedAt: new Date()
        },
        navigation: calculateNavigation(state.workflow.currentStep, {
          ...state.workflow.data,
          photos: filteredPhotos
        }, aiAnalysisEnabled)
      };

    case 'START_AI_ANALYSIS':
      if (!state.workflow) return state;
      return {
        ...state,
        workflow: {
          ...state.workflow,
          data: {
            ...state.workflow.data,
            aiAnalysis: {
              status: 'processing',
              results: [],
              combinedSuggestions: { tags: [], categories: [] },
              rejectedImages: []
            }
          },
          updatedAt: new Date()
        },
        isLoading: true
      };

    case 'AI_ANALYSIS_COMPLETE':
      if (!state.workflow) return state;
      
      // Ensure the AI analysis payload is clean
      const sanitizedAIAnalysis = action.payload;
      
      const metadataWithDefaults = {
        tags: sanitizedAIAnalysis.combinedSuggestions?.tags || [],
        categories: sanitizedAIAnalysis.combinedSuggestions?.categories || [],
        location: {
          address: '',
          suburb: '',
          council: '',
          postcode: '',
          coordinates: undefined
        },
        pickupDate: undefined,
        isValid: false,
        validationErrors: []
      };
      
      console.log('📋 Creating metadata with AI suggestions:', {
        tags: metadataWithDefaults.tags,
        categories: metadataWithDefaults.categories
      });
      
      // Determine next step based on analysis results
      const hasContentViolations = sanitizedAIAnalysis.rejectedImages?.length > 0 && 
        sanitizedAIAnalysis.rejectedImages.some((img: any) => 
          img.violations && !img.violations.includes('processing_error')
        );
      
      // Only advance to metadata_entry if no content violations
      const nextStepAfterAI = hasContentViolations ? 'ai_analysis' : 'metadata_entry';
      
      return {
        ...state,
        workflow: {
          ...state.workflow,
          currentStep: nextStepAfterAI,
          data: {
            ...state.workflow.data,
            aiAnalysis: sanitizedAIAnalysis,
            metadata: metadataWithDefaults
          },
          updatedAt: new Date()
        },
        isLoading: false,
        navigation: calculateNavigation(nextStepAfterAI, {
          ...state.workflow.data,
          aiAnalysis: sanitizedAIAnalysis,
          metadata: metadataWithDefaults
        }, aiAnalysisEnabled)
      };

    case 'AI_ANALYSIS_FAILED':
      if (!state.workflow) return state;
      return {
        ...state,
        workflow: {
          ...state.workflow,
          data: {
            ...state.workflow.data,
            aiAnalysis: {
              status: 'failed',
              results: [],
              combinedSuggestions: { tags: [], categories: [] },
              rejectedImages: [],
              error: action.payload
            }
          },
          updatedAt: new Date()
        },
        isLoading: false,
        error: action.payload
      };

    case 'SKIP_AI_ANALYSIS':
      if (!state.workflow) return state;
      const defaultMetadata = {
        tags: [],
        categories: [],
        location: {
          address: '',
          suburb: '',
          council: '',
          postcode: '',
          coordinates: undefined
        },
        pickupDate: undefined,
        isValid: false,
        validationErrors: []
      };
      
      return {
        ...state,
        workflow: {
          ...state.workflow,
          currentStep: 'metadata_entry',
          data: {
            ...state.workflow.data,
            aiAnalysis: {
              status: 'completed',
              results: [],
              combinedSuggestions: { tags: [], categories: [] },
              rejectedImages: []
            },
            metadata: defaultMetadata
          },
          updatedAt: new Date()
        },
        isLoading: false,
        navigation: calculateNavigation('metadata_entry', {
          ...state.workflow.data,
          aiAnalysis: {
            status: 'completed',
            results: [],
            combinedSuggestions: { tags: [], categories: [] },
            rejectedImages: []
          },
          metadata: defaultMetadata
        }, aiAnalysisEnabled)
      };

    case 'UPDATE_METADATA':
      if (!state.workflow || !state.workflow.data.metadata) return state;
      
      // Sanitize the metadata update
      const sanitizedPayload = { ...action.payload };
      
      // Handle pickupDate specifically
      if (sanitizedPayload.pickupDate !== undefined) {
        if (sanitizedPayload.pickupDate instanceof Date && isNaN(sanitizedPayload.pickupDate.getTime())) {
          // Remove invalid dates
          sanitizedPayload.pickupDate = undefined;
        }
      }
      
      const updatedMetadata = { ...state.workflow.data.metadata, ...sanitizedPayload };
      return {
        ...state,
        workflow: {
          ...state.workflow,
          data: {
            ...state.workflow.data,
            metadata: updatedMetadata
          },
          updatedAt: new Date()
        },
        navigation: calculateNavigation(state.workflow.currentStep, {
          ...state.workflow.data,
          metadata: updatedMetadata
        }, aiAnalysisEnabled)
      };

    case 'NEXT_STEP':
      if (!state.workflow || !state.navigation.canGoForward) return state;
      const nextStep = getNextStep(state.workflow.currentStep, aiAnalysisEnabled);
      return {
        ...state,
        workflow: {
          ...state.workflow,
          currentStep: nextStep,
          updatedAt: new Date()
        },
        navigation: calculateNavigation(nextStep, state.workflow.data, aiAnalysisEnabled)
      };

    case 'PREVIOUS_STEP':
      if (!state.workflow || !state.navigation.canGoBack) return state;
      const prevStep = getPreviousStep(state.workflow.currentStep, aiAnalysisEnabled);
      return {
        ...state,
        workflow: {
          ...state.workflow,
          currentStep: prevStep,
          updatedAt: new Date()
        },
        navigation: calculateNavigation(prevStep, state.workflow.data, aiAnalysisEnabled)
      };

    case 'CANCEL_WORKFLOW':
      return {
        ...state,
        workflow: state.workflow ? {
          ...state.workflow,
          status: 'cancelled',
          updatedAt: new Date()
        } : null
      };

    case 'COMPLETE_WORKFLOW':
      return {
        ...state,
        workflow: state.workflow ? {
          ...state.workflow,
          status: 'completed',
          updatedAt: new Date()
        } : null
      };

    default:
      return state;
  }
};

// Helper functions
const createNewWorkflow = (userId: string): SubmissionWorkflow => {
  const now = new Date();
  const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 hours
  
  return {
    id: `workflow_${Date.now()}_${Math.random().toString(36).substring(2)}`,
    userId,
    currentStep: 'photo_capture',
    status: 'active',
    createdAt: now,
    updatedAt: now,
    expiresAt: expiresAt,
    data: createEmptyWorkflowData()
  };
};

const createEmptyWorkflowData = (): SubmissionWorkflowData => ({
  photos: []
});

const getNextStep = (currentStep: SubmissionStep, aiAnalysisEnabled: boolean = true): SubmissionStep => {
  switch (currentStep) {
    case 'photo_capture': return aiAnalysisEnabled ? 'ai_analysis' : 'metadata_entry';
    case 'ai_analysis': return 'metadata_entry';
    case 'metadata_entry': return 'metadata_entry'; // Stay on last step
    default: return currentStep;
  }
};

const getPreviousStep = (currentStep: SubmissionStep, aiAnalysisEnabled: boolean = true): SubmissionStep => {
  switch (currentStep) {
    case 'metadata_entry': return aiAnalysisEnabled ? 'ai_analysis' : 'photo_capture';
    case 'ai_analysis': return 'photo_capture';
    case 'photo_capture': return 'photo_capture'; // Stay on first step
    default: return currentStep;
  }
};

const calculateNavigation = (currentStep: SubmissionStep, data: SubmissionWorkflowData, aiAnalysisEnabled: boolean = true): StepNavigation => {
  const steps: SubmissionStep[] = aiAnalysisEnabled 
    ? ['photo_capture', 'ai_analysis', 'metadata_entry']
    : ['photo_capture', 'metadata_entry'];
  const currentStepIndex = steps.indexOf(currentStep);
  
  let canGoForward = false;
  let canSubmit = false;

  switch (currentStep) {
    case 'photo_capture':
      canGoForward = data.photos.length > 0;
      break;
    case 'ai_analysis':
      canGoForward = data.aiAnalysis?.status === 'completed';
      break;
    case 'metadata_entry':
      canSubmit = data.metadata?.isValid === true;
      break;
  }

  return {
    canGoBack: currentStepIndex > 0,
    canGoForward,
    canSubmit,
    currentStepIndex,
    totalSteps: steps.length
  };
};

// Debounced persistence to prevent recursion
let persistenceTimeout: NodeJS.Timeout | null = null;
const debouncedPersistWorkflow = (workflow: SubmissionWorkflow) => {
  if (persistenceTimeout) {
    clearTimeout(persistenceTimeout);
  }
  
  persistenceTimeout = setTimeout(() => {
    persistWorkflowChange(workflow).catch(error => {
      console.error('Failed to persist workflow change:', error);
    });
  }, 1000); // Debounce for 1 second
};

// Helper function to persist workflow changes
const persistWorkflowChange = async (workflow: SubmissionWorkflow) => {
  try {
    // Validate workflow data before persisting
    if (!workflow || !workflow.id || !workflow.userId) {
      console.warn('Invalid workflow data, skipping persistence', { 
        hasWorkflow: !!workflow,
        hasId: workflow?.id,
        hasUserId: workflow?.userId 
      });
      return;
    }
    
    // Import sanitization utilities
    const { deepSanitizeData, validateFirestoreData, logDataStructure } = await import('../utils/dataSanitization');
    
    // Log the original data structure for debugging
    console.log('🔍 Persisting workflow data...');
    logDataStructure(workflow, 'Original Workflow');
    
    // Deep sanitize the workflow data
    const { sanitizedData, issues } = deepSanitizeData(workflow, 'workflow');
    
    if (issues.length > 0) {
      console.warn('🧹 Data sanitization issues found:', issues);
    }
    
    // Validate the sanitized data
    const { isValid, errors } = validateFirestoreData(sanitizedData);
    
    if (!isValid) {
      console.error('❌ Sanitized data still invalid for Firestore:', errors);
      logDataStructure(sanitizedData, 'Sanitized Workflow (Still Invalid)');
      return; // Skip persistence if data is still invalid
    }
    
    console.log('✅ Data sanitized successfully, persisting to Firestore');
    
    // Extract only the fields that should be updated (not the entire workflow)
    const updateFields: Partial<SubmissionWorkflow> = {
      currentStep: sanitizedData.currentStep,
      status: sanitizedData.status,
      data: sanitizedData.data,
      updatedAt: sanitizedData.updatedAt,
      // Don't include id, userId, createdAt, expiresAt as these shouldn't change
    };
    
    await WorkflowService.updateWorkflow(workflow.id, updateFields);
    
  } catch (error) {
    console.error('Failed to persist workflow change:', error);
    // Don't throw - we don't want to break the UI for persistence failures
    // The workflow can still function in memory even if persistence fails
  }
};

// Main hook
export const useSubmissionWorkflow = () => {
  const { user } = useFirebase();
  const { isEnabled } = useFeatureFlag();
  const [isInitializing, setIsInitializing] = useState(false);
  
  const aiAnalysisEnabled = isEnabled('AI_ANALYSIS_ENABLED');
  
  const [state, dispatch] = useReducer(
    (state: WorkflowState, action: WorkflowAction) => workflowReducer(state, action, aiAnalysisEnabled),
    {
      workflow: null,
      isLoading: false,
      error: null,
      navigation: {
        canGoBack: false,
        canGoForward: false,
        canSubmit: false,
        currentStepIndex: 0,
        totalSteps: aiAnalysisEnabled ? 3 : 2
      }
    }
  );

  // Initialize workflow when user is available
  useEffect(() => {
    if (user && !state.workflow && !isInitializing) {
      const initializeWorkflow = async () => {
        console.log('🚀 Initializing workflow for user:', user.uid);
        setIsInitializing(true);
        
        try {
          // Import debugging utilities
          const { logDataStructure } = await import('../utils/dataSanitization');
          
          // Create workflow in memory first
          const newWorkflow = createNewWorkflow(user.uid);
          console.log('✅ Created new workflow:', newWorkflow.id);
          
          // Log the initial workflow structure
          logDataStructure(newWorkflow, 'Initial Workflow');
          
          dispatch({ type: 'START_WORKFLOW', payload: newWorkflow });
          
          // Then persist to Firestore (non-blocking)
          try {
            await WorkflowService.saveWorkflow(newWorkflow);
            console.log('💾 Workflow saved to Firestore successfully');
          } catch (error) {
            console.error('❌ Failed to save initial workflow:', error);
            // Don't block the UI - workflow can still function without persistence
          }
        } catch (error) {
          console.error('❌ Failed to initialize workflow:', error);
        } finally {
          setIsInitializing(false);
          console.log('🏁 Workflow initialization complete');
        }
      };
      
      // Add a small delay to prevent React strict mode double initialization
      const timeoutId = setTimeout(initializeWorkflow, 10);
      return () => clearTimeout(timeoutId);
    }
  }, [user, state.workflow, isInitializing]);

  // Auto-cleanup expired workflows
  useEffect(() => {
    if (state.workflow && state.workflow.expiresAt < new Date()) {
      dispatch({ type: 'CANCEL_WORKFLOW' });
    }
  }, [state.workflow]);

  // Check if workflow is ready for operations
  const isWorkflowReady = useCallback(() => {
    const ready = state.workflow && 
                  state.workflow.id && 
                  state.workflow.userId && 
                  !isInitializing;
    
    if (ready && state.workflow) {
      // Quick validation check for common issues
      if (state.workflow.data?.aiAnalysis?.results) {
        const hasInvalidTimestamps = state.workflow.data.aiAnalysis.results.some(result => 
          result.analysisTimestamp && isNaN(new Date(result.analysisTimestamp).getTime())
        );
        
        if (hasInvalidTimestamps) {
          console.warn('⚠️ Workflow contains invalid timestamps but is marked as ready');
        }
      }
    }
    
    return ready;
  }, [state.workflow, isInitializing]);

  // Photo management
  const addPhoto = useCallback((photo: CapturedPhoto) => {
    if (!isWorkflowReady()) {
      console.warn('Workflow not ready for photo operations');
      return;
    }
    
    dispatch({ type: 'ADD_PHOTO', payload: photo });
    
    // Persist change
    const updatedWorkflow = {
      ...state.workflow!,
      data: {
        ...state.workflow!.data,
        photos: [...state.workflow!.data.photos.filter(p => p.id !== photo.id), photo]
      },
      updatedAt: new Date()
    };
    // Use debounced persistence to prevent recursion
    debouncedPersistWorkflow(updatedWorkflow);
  }, [state.workflow, isWorkflowReady]);

  const removePhoto = useCallback((photoId: string) => {
    if (!isWorkflowReady()) {
      console.warn('Workflow not ready for photo operations');
      return;
    }
    
    dispatch({ type: 'REMOVE_PHOTO', payload: photoId });
    
    // Persist change
    const updatedWorkflow = {
      ...state.workflow!,
      data: {
        ...state.workflow!.data,
        photos: state.workflow!.data.photos.filter(p => p.id !== photoId)
      },
      updatedAt: new Date()
    };
    // Use debounced persistence to prevent recursion
    debouncedPersistWorkflow(updatedWorkflow);
  }, [state.workflow, isWorkflowReady]);

  // AI Analysis with retry mechanism
  const startAIAnalysis = useCallback(async (retryCount = 0) => {
    if (!state.workflow || state.workflow.data.photos.length === 0) {
      return;
    }

    // Check if all photos are uploaded to Firebase Storage
    const unuploadedPhotos = state.workflow.data.photos.filter(photo => !photo.isUploaded);
    if (unuploadedPhotos.length > 0) {
      dispatch({ 
        type: 'AI_ANALYSIS_FAILED', 
        payload: `Please wait for ${unuploadedPhotos.length} photo(s) to finish uploading before starting AI analysis.` 
      });
      return;
    }

    const maxRetries = 3;
    const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 10000); // Exponential backoff, max 10s

    dispatch({ type: 'START_AI_ANALYSIS' });

    try {
      // Prepare images for AI analysis - all should now have Firebase Storage URLs
      const images = state.workflow.data.photos.map(photo => ({
        id: photo.id,
        filename: photo.filename,
        url: photo.url, // This should now be a Firebase Storage URL
        size: photo.size,
        mimeType: 'image/jpeg' // Default to JPEG, could be enhanced to detect actual type
      }));

      // Generate unique request ID
      const requestId = `analysis_${Date.now()}_${Math.random().toString(36).substring(2)}`;

      // Call Firebase Function for AI analysis
      const analyzeImagesWithAI = httpsCallable(functions, 'analyzeImagesWithAI');
      const response = await analyzeImagesWithAI({
        images,
        requestId,
        userId: user?.uid
      });

      const analysisResponse = response.data as Omit<ImageAnalysisResponse, 'results'> & {
        results: Array<{
          imageId: string;
          isCompliant: boolean;
          nonComplianceReason?: string;
          suggestedTags: string[];
          suggestedCategories: string[];
          confidence: number;
          analysisTimestamp: { seconds: number; nanoseconds: number };
        }>;
        aiStatus: 'success' | 'mock' | 'failed' | 'unavailable';
        statusMessage?: string;
      };

      // Check AI status and handle accordingly
      if (analysisResponse.aiStatus !== 'success') {
        console.log(`🤖 AI Analysis status: ${analysisResponse.aiStatus}`, {
          statusMessage: analysisResponse.statusMessage,
          requestId: analysisResponse.requestId
        });
        
        // For non-success statuses, we still process the results but show a friendly message
        if (analysisResponse.statusMessage) {
          // Store the status message to show to the user
          console.log('📝 AI Status Message:', analysisResponse.statusMessage);
        }
      }

      // Convert Firebase Timestamp to Date with validation
      const results = analysisResponse.results.map((result, index) => {
        let analysisTimestamp: Date;
        
        try {
          // Handle different timestamp formats
          if (result.analysisTimestamp && typeof result.analysisTimestamp === 'object') {
            // Firebase Timestamp format
            if (typeof result.analysisTimestamp.seconds === 'number') {
              analysisTimestamp = new Date(result.analysisTimestamp.seconds * 1000);
            } else if ((result.analysisTimestamp as any).toDate && typeof (result.analysisTimestamp as any).toDate === 'function') {
              // Firestore Timestamp object
              analysisTimestamp = (result.analysisTimestamp as any).toDate();
            } else {
              throw new Error('Invalid timestamp format');
            }
          } else if (result.analysisTimestamp && (result.analysisTimestamp as any) instanceof Date) {
            // Already a Date object
            analysisTimestamp = result.analysisTimestamp;
          } else if (typeof result.analysisTimestamp === 'string') {
            // ISO string format
            analysisTimestamp = new Date(result.analysisTimestamp);
          } else {
            throw new Error('No valid timestamp found');
          }
          
          // Validate the resulting date
          if (isNaN(analysisTimestamp.getTime())) {
            throw new Error('Invalid date after conversion');
          }
          
          console.log(`✅ Successfully converted timestamp for result ${index}:`, analysisTimestamp.toISOString());
          
        } catch (error) {
          console.warn(`⚠️ Failed to convert timestamp for result ${index}:`, error, 'Using current time as fallback');
          analysisTimestamp = new Date(); // Fallback to current time
        }
        
        return {
          ...result,
          analysisTimestamp
        };
      });

      // Import sanitization utilities
      const { deepSanitizeData, logDataStructure } = await import('../utils/dataSanitization');
      
      const analysisResult: AIAnalysisStepData = {
        status: 'completed',
        aiStatus: analysisResponse.aiStatus,
        statusMessage: analysisResponse.statusMessage,
        results,
        combinedSuggestions: {
          tags: analysisResponse.combinedTags,
          categories: analysisResponse.combinedCategories
        },
        rejectedImages: analysisResponse.rejectedImages,
        processingTimeMs: analysisResponse.processingTimeMs,
        userMessage: analysisResponse.userMessage
      };

      console.log('🔍 AI Analysis result before sanitization:');
      logDataStructure(analysisResult, 'AI Analysis Result');

      // Deep sanitize the analysis result to ensure no invalid dates
      const { sanitizedData: sanitizedAnalysisResult, issues } = deepSanitizeData(analysisResult, 'aiAnalysis');
      
      if (issues.length > 0) {
        console.warn('🧹 AI Analysis sanitization issues found:', issues);
      }

      console.log('✅ AI Analysis result after sanitization:');
      logDataStructure(sanitizedAnalysisResult, 'Sanitized AI Analysis Result');

      dispatch({ type: 'AI_ANALYSIS_COMPLETE', payload: sanitizedAnalysisResult });
    } catch (error) {
      console.error(`❌ AI analysis failed (attempt ${retryCount + 1}):`, error);
      
      let errorMessage = 'AI analysis failed';
      let shouldRetry = false;
      
      // Handle specific Firebase Function errors
      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as { code: string; message: string };
        switch (firebaseError.code) {
          case 'unauthenticated':
            errorMessage = 'Please sign in to use AI analysis';
            break;
          case 'permission-denied':
            errorMessage = 'You do not have permission to use AI analysis';
            break;
          case 'unavailable':
          case 'deadline-exceeded':
          case 'internal':
            // These errors are retryable
            shouldRetry = retryCount < maxRetries;
            errorMessage = shouldRetry 
              ? `AI analysis failed, retrying in ${retryDelay / 1000}s... (${retryCount + 1}/${maxRetries + 1})`
              : firebaseError.code === 'unavailable' 
                ? 'AI analysis service is temporarily unavailable. You can continue without AI suggestions.'
                : firebaseError.code === 'deadline-exceeded'
                  ? 'AI analysis timed out. You can continue without AI suggestions.'
                  : 'AI analysis failed due to internal error. You can continue without AI suggestions.';
            break;
          default:
            errorMessage = firebaseError.message || 'AI analysis failed';
        }
      } else if (error instanceof Error) {
        // Check if it's a timestamp conversion error
        if (error.message.includes('timestamp') || error.message.includes('Invalid date')) {
          console.error('🕐 Timestamp conversion error in AI analysis:', error);
          errorMessage = 'AI analysis completed but had timestamp issues. You can continue with manual entry.';
        } else {
          // Network errors and other exceptions might be retryable
          shouldRetry = retryCount < maxRetries;
          errorMessage = shouldRetry 
            ? `AI analysis failed, retrying in ${retryDelay / 1000}s... (${retryCount + 1}/${maxRetries + 1})`
            : `${error.message}. You can continue without AI suggestions.`;
        }
      }

      if (shouldRetry) {
        console.log(`🔄 Retrying AI analysis in ${retryDelay / 1000}s...`);
        // Wait before retrying
        setTimeout(() => {
          startAIAnalysis(retryCount + 1);
        }, retryDelay);
      } else {
        console.log('❌ AI analysis failed permanently, allowing manual entry');
        dispatch({ type: 'AI_ANALYSIS_FAILED', payload: errorMessage });
      }
    }
  }, [state.workflow, user]);

  // Metadata management
  const updateMetadata = useCallback(async (metadata: Partial<MetadataStepData>) => {
    if (!isWorkflowReady() || !state.workflow!.data.metadata) {
      console.warn('Workflow not ready for metadata operations');
      return;
    }
    
    console.log('📝 Updating metadata:', metadata);
    
    // Import sanitization utilities
    const { deepSanitizeData } = await import('../utils/dataSanitization');
    
    // Deep sanitize the metadata update
    const { sanitizedData: sanitizedMetadata, issues } = deepSanitizeData(metadata, 'metadata_update');
    
    if (issues.length > 0) {
      console.warn('🧹 Metadata sanitization issues:', issues);
    }
    
    console.log('📝 Sanitized metadata:', sanitizedMetadata);
    
    dispatch({ type: 'UPDATE_METADATA', payload: sanitizedMetadata });
    
    // Persist change with sanitized data
    const updatedWorkflow = {
      ...state.workflow!,
      data: {
        ...state.workflow!.data,
        metadata: { ...state.workflow!.data.metadata, ...sanitizedMetadata }
      },
      updatedAt: new Date()
    };
    
    // Use debounced persistence to prevent recursion
    debouncedPersistWorkflow(updatedWorkflow);
  }, [state.workflow, isWorkflowReady]);

  const validateMetadata = useCallback((metadata: MetadataStepData): string[] => {
    const errors: string[] = [];

    // Validate tags
    if (!metadata.tags || metadata.tags.length === 0) {
      errors.push('At least one tag is required');
    }

    // Validate location - ensure location object exists
    if (!metadata.location) {
      errors.push('Location information is required');
      return errors; // Return early if no location object
    }

    if (!metadata.location.address || metadata.location.address.trim() === '') {
      errors.push('Address is required');
    }

    if (!metadata.location.suburb || metadata.location.suburb.trim() === '') {
      errors.push('Suburb is required');
    }

    if (!metadata.location.council || metadata.location.council.trim() === '') {
      errors.push('Council is required');
    }

    if (!metadata.location.postcode || metadata.location.postcode.trim() === '') {
      errors.push('Postcode is required');
    }

    if (!metadata.location.coordinates || 
        typeof metadata.location.coordinates.lat !== 'number' || 
        typeof metadata.location.coordinates.lng !== 'number') {
      errors.push('Location coordinates are required');
    }

    // Validate pickup date if provided
    if (metadata.pickupDate) {
      if (!(metadata.pickupDate instanceof Date) || isNaN(metadata.pickupDate.getTime())) {
        errors.push('Invalid pickup date format');
      } else if (metadata.pickupDate <= new Date()) {
        errors.push('Pickup date must be in the future');
      }
    }

    return errors;
  }, []);

  // Navigation
  const goToNextStep = useCallback(() => {
    if (state.navigation.canGoForward) {
      if (state.workflow?.currentStep === 'photo_capture') {
        // Check if AI analysis is enabled
        if (isEnabled('AI_ANALYSIS_ENABLED')) {
          // Automatically start AI analysis when moving from photo capture
          startAIAnalysis();
        } else {
          // Skip AI analysis and go directly to metadata entry
          dispatch({ type: 'SKIP_AI_ANALYSIS' });
        }
      } else {
        dispatch({ type: 'NEXT_STEP' });
      }
    }
  }, [state.navigation.canGoForward, state.workflow?.currentStep, startAIAnalysis, isEnabled]);

  const goToPreviousStep = useCallback(() => {
    if (state.navigation.canGoBack) {
      dispatch({ type: 'PREVIOUS_STEP' });
    }
  }, [state.navigation.canGoBack]);

  // Workflow management
  const cancelWorkflow = useCallback(async () => {
    dispatch({ type: 'CANCEL_WORKFLOW' });
    
    // Persist cancellation
    if (state.workflow) {
      try {
        await WorkflowService.cancelWorkflow(state.workflow.id);
      } catch (error) {
        console.error('Failed to cancel workflow in Firestore:', error);
      }
    }
  }, [state.workflow]);

  const submitWorkflow = useCallback(async () => {
    if (!state.workflow || !state.navigation.canSubmit || !user) {
      console.warn('Cannot submit workflow:', {
        hasWorkflow: !!state.workflow,
        canSubmit: state.navigation.canSubmit,
        hasUser: !!user
      });
      return null;
    }

    try {
      // Import debugging utilities
      const { logDataStructure, validateFirestoreData } = await import('../utils/dataSanitization');
      
      console.log('🚀 Starting workflow submission...');
      console.log('Workflow ID:', state.workflow.id);
      console.log('User ID:', user.uid);
      
      // Ensure the latest workflow data is persisted before submission
      console.log('💾 Ensuring workflow data is persisted before submission...');
      await persistWorkflowChange(state.workflow);
      
      // Add a small delay to ensure Firestore consistency
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Log the complete workflow state for debugging
      logDataStructure(state.workflow, 'Workflow Before Submission');
      
      // Specifically log the metadata
      if (state.workflow.data.metadata) {
        console.group('📋 Metadata Details');
        console.log('Tags:', state.workflow.data.metadata.tags);
        console.log('Categories:', state.workflow.data.metadata.categories);
        console.log('Location:', state.workflow.data.metadata.location);
        console.log('Pickup Date:', state.workflow.data.metadata.pickupDate);
        console.log('Is Valid:', state.workflow.data.metadata.isValid);
        console.log('Validation Errors:', state.workflow.data.metadata.validationErrors);
        console.groupEnd();
      }
      
      // Validate the workflow data
      const { isValid, errors } = validateFirestoreData(state.workflow);
      if (!isValid) {
        console.error('❌ Workflow data contains invalid elements:', errors);
        
        // Apply deep sanitization to fix the issues
        console.log('🧹 Attempting to sanitize workflow data before submission...');
        const { deepSanitizeData } = await import('../utils/dataSanitization');
        const { sanitizedData: sanitizedWorkflow, issues } = deepSanitizeData(state.workflow, 'workflow_submission');
        
        if (issues.length > 0) {
          console.warn('🧹 Workflow sanitization issues resolved:', issues);
        }
        
        // Validate the sanitized data
        const { isValid: sanitizedIsValid, errors: sanitizedErrors } = validateFirestoreData(sanitizedWorkflow);
        if (!sanitizedIsValid) {
          console.error('❌ Even after sanitization, workflow data is invalid:', sanitizedErrors);
          throw new Error('Workflow data contains invalid elements that cannot be automatically fixed');
        }
        
        console.log('✅ Workflow data sanitized successfully');
        
        // Update the workflow state with sanitized data (for future operations)
        // Note: We don't dispatch here to avoid side effects during submission
      }

      // Call Firebase Function to submit workflow report
      const submitWorkflowReport = httpsCallable(functions, 'submitWorkflowReport');
      const response = await submitWorkflowReport({
        workflowId: state.workflow.id
      });

      const result = response.data as { reportId: string; success: boolean };
      
      if (result.success) {
        console.log('✅ Workflow submitted successfully:', result.reportId);
        dispatch({ type: 'COMPLETE_WORKFLOW' });
        
        return {
          reportId: result.reportId,
          success: true
        };
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('❌ Workflow submission failed:', error);
      
      let errorMessage = 'Submission failed';
      
      // Handle specific Firebase Function errors
      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as { code: string; message: string };
        switch (firebaseError.code) {
          case 'unauthenticated':
            errorMessage = 'Please sign in to submit your report';
            break;
          case 'permission-denied':
            errorMessage = 'You do not have permission to submit this report';
            break;
          case 'not-found':
            errorMessage = 'Workflow not found. Please start over.';
            break;
          case 'failed-precondition':
            if (firebaseError.message.includes('metadata is not valid')) {
              errorMessage = 'Please complete all required fields: address, suburb, council, postcode, and at least one tag';
            } else {
              errorMessage = 'Please complete all required fields before submitting';
            }
            break;
          case 'unavailable':
            errorMessage = 'Service temporarily unavailable. Please try again.';
            break;
          default:
            errorMessage = firebaseError.message || 'Submission failed';
        }
      } else if (error instanceof Error) {
        if (error.message.includes('metadata is not valid')) {
          errorMessage = 'Please complete all required fields: address, suburb, council, postcode, and at least one tag';
        } else {
          errorMessage = error.message;
        }
      }
      
      throw new Error(errorMessage);
    }
  }, [state.workflow, state.navigation.canSubmit, user]);

  // Retry AI Analysis
  const retryAIAnalysis = useCallback(() => {
    startAIAnalysis(0); // Reset retry count
  }, [startAIAnalysis]);

  // Skip AI Analysis
  const skipAIAnalysis = useCallback(() => {
    dispatch({ type: 'SKIP_AI_ANALYSIS' });
  }, []);

  // Cleanup debounced persistence on unmount
  useEffect(() => {
    return () => {
      if (persistenceTimeout) {
        clearTimeout(persistenceTimeout);
      }
    };
  }, []);

  return {
    // State
    workflow: state.workflow,
    isLoading: state.isLoading || isInitializing,
    error: state.error,
    navigation: state.navigation,
    isInitializing,
    isWorkflowReady: isWorkflowReady(),
    
    // Photo management
    addPhoto,
    removePhoto,
    
    // AI Analysis
    startAIAnalysis,
    retryAIAnalysis,
    skipAIAnalysis,
    
    // Metadata management
    updateMetadata,
    validateMetadata,
    
    // Navigation
    goToNextStep,
    goToPreviousStep,
    
    // Workflow management
    cancelWorkflow,
    submitWorkflow
  };
};